import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CartService, type ReleaseSeatsBody } from './cart.service';

interface StoredSeatHold extends ReleaseSeatsBody {
  expiryAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class SeatHoldReleaseService {
  private static readonly STORAGE_KEY = 'seat_hold_release';
  private static readonly HOLD_MS = 10 * 60 * 1000;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private releasing = false;

  constructor(private cartService: CartService) {
    queueMicrotask(() => this.restore());
  }

  /** Start 10-min release timer after hold-seats succeeds. */
  arm(payload: ReleaseSeatsBody): void {
    const normalized = this.normalizePayload(payload);
    if (!normalized) return;

    const existingExpiry = Number(sessionStorage.getItem('review_expiry_at') ?? '');
    const expiryAt =
      Number.isFinite(existingExpiry) && existingExpiry > Date.now()
        ? existingExpiry
        : Date.now() + SeatHoldReleaseService.HOLD_MS;

    const record: StoredSeatHold = { ...normalized, expiryAt };
    try {
      sessionStorage.setItem(SeatHoldReleaseService.STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* ignore */
    }
    this.schedule(expiryAt);
  }

  /** Call after successful booking/payment so seats are not released. */
  markBooked(): void {
    this.clearTimer();
    this.clearStored();
  }

  /**
   * Release held seats via POST /seatsio/release-seats.
   * Body: { show_id, seat_ids, hold_token }
   * Sources (in order): explicit payload → seat_hold_release storage → checkout session.
   */
  async releaseIfUnbooked(override?: Partial<ReleaseSeatsBody> | null): Promise<boolean> {
    const record = this.resolveReleasePayload(override);
    if (!record) return false;
    this.clearTimer();
    return this.postRelease(record);
  }

  private resolveReleasePayload(
    override?: Partial<ReleaseSeatsBody> | null
  ): ReleaseSeatsBody | null {
    const fromOverride = this.normalizePayload(override);
    if (fromOverride) return fromOverride;

    const stored = this.readStored();
    if (stored) {
      return {
        show_id: stored.show_id,
        seat_ids: stored.seat_ids,
        hold_token: stored.hold_token
      };
    }

    return this.readFromCheckoutSession();
  }

  private normalizePayload(
    payload: Partial<ReleaseSeatsBody> | null | undefined
  ): ReleaseSeatsBody | null {
    if (!payload) return null;
    const holdToken = String(payload.hold_token ?? '').trim();
    const showId = payload.show_id;
    const seatIds = Array.isArray(payload.seat_ids)
      ? payload.seat_ids.filter((id) => id != null && id !== '')
      : [];
    if (!holdToken || showId == null || showId === '' || !seatIds.length) return null;
    return { show_id: showId, seat_ids: seatIds, hold_token: holdToken };
  }

  private readFromCheckoutSession(): ReleaseSeatsBody | null {
    try {
      const raw = sessionStorage.getItem('checkout');
      if (!raw) return null;
      const checkout = JSON.parse(raw) as Record<string, unknown>;
      if (!checkout || typeof checkout !== 'object') return null;

      const holdToken = String(
        checkout['hold_token'] ?? checkout['seatsio_hold_token'] ?? checkout['seatsioHoldToken'] ?? ''
      ).trim();
      const showId =
        checkout['showId'] ??
        checkout['show_id'] ??
        (checkout['show'] && typeof checkout['show'] === 'object'
          ? (checkout['show'] as Record<string, unknown>)['id'] ??
            (checkout['show'] as Record<string, unknown>)['show_id']
          : null);
      const seatIdsRaw = checkout['seat_ids'] ?? checkout['ids'];
      const seatIds = Array.isArray(seatIdsRaw)
        ? seatIdsRaw.filter((id) => id != null && id !== '')
        : [];

      return this.normalizePayload({
        show_id: showId as string | number,
        seat_ids: seatIds as Array<number | string>,
        hold_token: holdToken
      });
    } catch {
      return null;
    }
  }

  private restore(): void {
    const record = this.readStored();
    if (!record) return;
    this.schedule(record.expiryAt);
  }

  private schedule(expiryAt: number): void {
    this.clearTimer();
    const delay = Math.max(0, expiryAt - Date.now());
    this.timer = setTimeout(() => {
      void this.releaseIfUnbooked();
    }, delay);
  }

  private async postRelease(record: ReleaseSeatsBody): Promise<boolean> {
    if (this.releasing) return false;
    this.releasing = true;
    this.clearStored();
    try {
      await firstValueFrom(
        this.cartService.releaseSeats({
          show_id: record.show_id,
          seat_ids: record.seat_ids,
          hold_token: record.hold_token
        })
      );
      return true;
    } catch (err) {
      console.error('[SeatHold] release-seats failed:', err);
      return false;
    } finally {
      this.releasing = false;
    }
  }

  private readStored(): StoredSeatHold | null {
    try {
      const raw = sessionStorage.getItem(SeatHoldReleaseService.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredSeatHold;
      if (!parsed?.hold_token || !parsed?.show_id || !Array.isArray(parsed.seat_ids)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private clearStored(): void {
    try {
      sessionStorage.removeItem(SeatHoldReleaseService.STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  private clearTimer(): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
